import { Button, Css, Palette, useModal, useSnackbar, useTestIds } from "@homebound/beam";
import { createObjectState } from "@homebound/form-state";
import { Icon } from "src/components";
import { BidPackageDetailPageDocument } from "src/routes/bids/BidPackageDetailPage.generated";
import { useSaveBidPackageMutation } from "src/routes/bids/components/EditBidPackageModal.generated";
import { useConfigureBidGroupsStepLazyQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { AddOptionModal } from "src/routes/bids/plan-based-bid-package/components/AddOptionModal";
import { mapToBidPlanGroupsInput, planBasedBidPackageFormConfig } from "src/routes/bids/plan-based-bid-package/utils";
import { createProductOfferingScopeUrl } from "src/RouteUrls";
import { openNewTab } from "src/utils/window";
import {
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupsDocument,
  PlanBasedBidsTableV2_MissingBidPlanOptionFragment,
} from "./PlanBasedBidsTableV2.generated";

type MissingOptionsCardProps = { bidPackage: PlanBasedBidsTableV2_BidPackageFragment };

export function MissingOptionsCard({ bidPackage }: MissingOptionsCardProps) {
  const tid = useTestIds({});
  const { openModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const [loadConfig] = useConfigureBidGroupsStepLazyQuery();
  const [saveBidPackage] = useSaveBidPackageMutation({
    refetchQueries: [BidPackageDetailPageDocument, PlanBasedBidsTableV2_BidPlanChildGroupsDocument],
  });
  const missingOptions = bidPackage.optionsMissingFromPackage;
  if (missingOptions.isEmpty) return null;

  async function handleAddOption(missingOption: PlanBasedBidsTableV2_MissingBidPlanOptionFragment) {
    const { data } = await loadConfig({
      variables: { bidPackageId: bidPackage.id, productOfferingIds: bidPackage.productOfferings.map((pof) => pof.id) },
    });
    if (!data) return;
    // A form state seeded from the package's current groups, so AddOptionModal appends the new option to the full list
    const formState = createObjectState(planBasedBidPackageFormConfig, {
      bidPackageId: bidPackage.id,
      bidPlanGroups: data.bidPackage.bidPlanGroups.map((bpg) => ({
        id: bpg.id,
        globalOptionId: bpg.globalOption?.id,
        primaryOptionGroupId: bpg.primaryOptionGroup?.id,
        order: bpg.order,
        dimensions: bpg.dimensions.map((d) => ({
          id: d.id,
          globalOptionTypeId: d.globalOptionType?.id,
          globalOptionGroupId: d.globalOptionGroup?.id,
          globalOptionIds: d.globalOptions.map((go) => go.id),
        })),
      })),
    });
    openModal({
      content: (
        <AddOptionModal
          formState={formState}
          dimensionResults={data.potentialBidPlanDimensions}
          potentialBidPlanOptions={data.bidPackage.potentialBidPlanOptions}
          defaultSelectedOptionIds={[missingOption.globalOption.id]}
          onSaved={async () => {
            await saveBidPackage({
              variables: {
                input: {
                  id: bidPackage.id,
                  bidPlanGroups: mapToBidPlanGroupsInput(formState.bidPlanGroups.value ?? []),
                },
              },
            });
            triggerNotice({ message: `${missingOption.globalOption.name} added to the bid.` });
          }}
        />
      ),
    });
  }

  return (
    <div {...tid.card} css={Css.ba.bcOrange700.bw2.br12.bgWhite.mt1.mb3.$}>
      <div css={Css.df.gap1.p2.bb.bcGray200.$}>
        <div>
          <Icon icon="error" color={Palette.Orange700} />
        </div>
        <div css={Css.df.fdc.$}>
          <span css={Css.smSb.pb1.gray900.$}>Options Missing From Bid Package</span>
          <span css={Css.sm.gray700.$}>The options listed below contain scope for this cost code.</span>
        </div>
      </div>
      {missingOptions.map((option) => (
        <div key={option.globalOption.id} {...tid.row} css={Css.df.aic.jcsb.gap2.p2.bt.bcOrange700.$}>
          <div css={Css.df.fdc.pl4.$}>
            <span css={Css.smSb.gray900.pb1.$}>{option.globalOption.name}</span>
            <div css={Css.xs.gray700.$}>
              Scope detected in{" "}
              {option.productOfferings.map((pof, i) => {
                const rpav = pof.aggregateActive;
                return (
                  <span key={pof.id}>
                    {i > 0 && ", "}
                    {rpav ? (
                      <span
                        css={Css.xsSb.blue700.cursorPointer.$}
                        onClick={() =>
                          openNewTab(
                            createProductOfferingScopeUrl(pof.id, rpav.id, {
                              filter: { costCode: bidPackage.costCodes.map((cc) => cc.id) },
                            }),
                          )
                        }
                      >
                        {pof.displayName} v{rpav.version}
                      </span>
                    ) : (
                      pof.displayName
                    )}
                  </span>
                );
              })}
            </div>
          </div>
          <Button variant="secondary" icon="plus" label="Add to Bid" onClick={() => handleAddOption(option)} />
        </div>
      ))}
    </div>
  );
}
